import React from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { SupabaseIcon } from '@/components/SupabaseIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from '@/components/ui/breadcrumb';
import {
  ChevronLeft,
  ChevronRight,
  Home,
  Clock,
  Trophy,
  Target,
  Edit3,
  Sparkles,
  CheckCircle
} from 'lucide-react';
import { useWorkshopProgress, WorkshopStep } from '@/contexts/WorkshopProgressContext';
import { cn } from '@/lib/utils';

interface WorkshopNavigationProps {
  className?: string;
  showHeader?: boolean;
  showBreadcrumbs?: boolean;
  showProgress?: boolean;
  showStats?: boolean;
}

const STEP_CONFIG = {
  landing: {
    title: 'Workshop Overview',
    description: 'Introduction to AI Quality Workshop',
    icon: Home,
    color: 'bg-blue-100 text-blue-700'
  },
  challenge: {
    title: 'The Challenge',
    description: 'Identify AI output problems',
    icon: Target,
    color: 'bg-orange-100 text-orange-700'
  },
  practice: {
    title: 'Hands-On Practice',
    description: 'Practice improvement techniques',
    icon: Edit3,
    color: 'bg-purple-100 text-purple-700'
  },
  transformation: {
    title: 'Transformation Reveal',
    description: 'See professional results',
    icon: Sparkles,
    color: 'bg-green-100 text-green-700'
  },
  assessment: {
    title: 'Skills Assessment',
    description: 'Validate your mastery',
    icon: Trophy,
    color: 'bg-yellow-100 text-yellow-700'
  },
  cta: {
    title: 'Complete Transformation',
    description: 'Next steps in your journey',
    icon: CheckCircle,
    color: 'bg-green-100 text-green-700'
  }
};

export const WorkshopNavigation: React.FC<WorkshopNavigationProps> = ({
  className,
  showHeader = true,
  showBreadcrumbs = true,
  showProgress = true,
  showStats = true
}) => {
  const {
    state,
    navigateToStep,
    getNextStep,
    getPreviousStep,
    canNavigateNext,
    canNavigatePrevious,
    getStepProgress,
    getMasteredSkillsCount,
    getWorkshopDuration
  } = useWorkshopProgress();

  const { current, total } = getStepProgress();
  const nextStep = getNextStep();
  const previousStep = getPreviousStep();
  const masteredSkills = getMasteredSkillsCount();
  const duration = getWorkshopDuration();

  const handleNext = () => {
    if (nextStep && canNavigateNext()) {
      navigateToStep(nextStep);
    }
  };

  const handlePrevious = () => {
    if (previousStep && canNavigatePrevious()) {
      navigateToStep(previousStep);
    }
  };

  const currentStepConfig = STEP_CONFIG[state.currentStep];
  const IconComponent = currentStepConfig.icon;

  return (
    <div className={cn('bg-white border-b border-gray-200 sticky top-0 z-40', className)}>
      <div className="container mx-auto px-4 py-4 max-w-6xl">
        {/* Header Section */}
        {showHeader && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
                <SupabaseIcon
                  iconPath="lyra-avatar.png"
                  alt="Lyra AI Mentor"
                  size="md"
                  className="w-8 h-8"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Live AI Quality Workshop</h1>
                <p className="text-sm text-gray-600">Interactive 5-minute transformation experience</p>
              </div>
            </div>

            {/* Workshop Stats */}
            {showStats && (
              <div className="hidden md:flex items-center space-x-6 text-sm">
                {duration && (
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{duration}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2 text-purple-600">
                  <Trophy className="w-4 h-4" />
                  <span>{masteredSkills}/5 Skills</span>
                </div>
                <Badge variant="outline" className="text-purple-700 border-purple-200">
                  Step {current} of {total}
                </Badge>
              </div>
            )}
          </div>
        )}

        {/* Breadcrumb Navigation */}
        {showBreadcrumbs && (
          <div className="mb-4">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() => navigateToStep('landing')}
                    className="cursor-pointer flex items-center space-x-2 hover:text-purple-600"
                  >
                    <Home className="w-4 h-4" />
                    <span>Workshop</span>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="flex items-center space-x-2 text-purple-700 font-medium">
                    <IconComponent className="w-4 h-4" />
                    <span>{currentStepConfig.title}</span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        )}

        {/* Progress Bar */}
        {showProgress && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{currentStepConfig.title}</h2>
                <p className="text-sm text-gray-600">{currentStepConfig.description}</p>
              </div>
              <div className={cn('px-3 py-1 rounded-full text-sm font-medium', currentStepConfig.color)}>
                <IconComponent className="w-4 h-4 inline mr-1" />
                Step {current}
              </div>
            </div>
            <Progress
              value={(current / total) * 100}
              className="h-3 mb-3 bg-gray-100"
            />

            {/* Step Navigation Pills */}
            <div className="flex items-center justify-center space-x-2 overflow-x-auto pb-2">
              {(Object.keys(STEP_CONFIG) as WorkshopStep[]).map((step, index) => {
                const stepConfig = STEP_CONFIG[step];
                const StepIcon = stepConfig.icon;
                const isCompleted = state.completedSteps.includes(step);
                const isCurrent = state.currentStep === step;
                const isAccessible = index <= current || isCompleted;

                return (
                  <Button
                    key={step}
                    variant={isCurrent ? 'default' : isCompleted ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => isAccessible && navigateToStep(step)}
                    disabled={!isAccessible}
                    className={cn(
                      'flex items-center space-x-2 whitespace-nowrap transition-all',
                      isCurrent && 'bg-purple-600 text-white shadow-md',
                      isCompleted && 'bg-green-100 text-green-700 hover:bg-green-200',
                      !isAccessible && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <StepIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">{stepConfig.title}</span>
                    {isCompleted && <CheckCircle className="w-3 h-3 ml-1" />}
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation Controls */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={!canNavigatePrevious()}
            className="flex items-center space-x-2"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Previous</span>
            {previousStep && (
              <span className="hidden sm:inline text-gray-500">
                ({STEP_CONFIG[previousStep].title})
              </span>
            )}
          </Button>

          <div className="flex items-center space-x-4">
            {/* Quick Stats for Mobile */}
            <div className="md:hidden flex items-center space-x-3 text-sm text-gray-600">
              {duration && (
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{duration}</span>
                </div>
              )}
              <div className="flex items-center space-x-1">
                <Trophy className="w-3 h-3" />
                <span>{masteredSkills}/5</span>
              </div>
            </div>

            {/* Reset Workshop Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm('Are you sure you want to restart the workshop? All progress will be lost.')) {
                  // resetWorkshop(); // Uncomment if you want reset functionality
                  navigateToStep('landing');
                }
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              Restart
            </Button>
          </div>

          <Button
            onClick={handleNext}
            disabled={!canNavigateNext() || !nextStep}
            className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700"
          >
            <span>Next</span>
            {nextStep && (
              <span className="hidden sm:inline text-purple-100">
                ({STEP_CONFIG[nextStep].title})
              </span>
            )}
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkshopNavigation;